import React, { Fragment, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, } from 'react';
import { useReducedMotion } from '@core/accessibility/hooks/useReducedMotion';
import { isTruthy } from '@core/helpers/boolean';
import { useGlobalStore } from '@core/hooks/useGlobalStore';
import { useContextTranslation } from '@core/i18n';
import { playerStateOpenedSelector } from '@core/player/model';
import { PlayerLayout } from '@core/player/types';
import { cartDisplayedCallbackSelector } from '@core/shopping/model';
import { WidgetSizeEnum } from '@core/types';
import { IPresentation } from '@core/types';
import { useEmbedFeedStore } from '@core/widgets/embedFeed/store';
import { useTrackEmbedImpression } from '@core/widgets/tracking';
import { useHandleEmbedEngagedVideo } from '@core/widgets/tracking/useHandleEmbedEngagedVideo';
import { useTrackEmbedInteractableEngagedVideo } from '@core/widgets/tracking/useTrackEmbedInteractableEngagedVideo';
import { useTrackThumbnailImpressionsEmbedFeed } from '@core/widgets/tracking/useTrackThumbnailImpressionsEmbedFeed';
import { assetPinnedLeftArrow, assetPinnedRightArrow } from '@embed/assets.fwn';
import { useGridAutoplayVideoId } from '@embed/components/Grid/hooks/autoplay/useGridAutoplayVideoId';
import { useUpdateGridAutoplay } from '@embed/components/Grid/hooks/autoplay/useUpdateGridAutoplay';
import { ThumbnailContainerV2 } from '@embed/components/Grid/ThumbnailV2/ThumbnailContainerV2';
import { EventType } from '@embed/customEmbedEvent';
import { useDispatchEmbedImpression } from '@embed/hooks/useDispatchEmbedImpression';
import { getPresentationFromParams } from '@embed/hooks/useEmbedAppContext/getPresentationFromParams';
import { usePlayerStateListener } from '@embed/hooks/usePlayerStateListener';
import { filterCurrentInViewport, sortByOtherArray } from '@embed/tools/utils';
import { PinnedWrapper } from './mode/PinnedWrapper';
import { PlayerDeckContainer } from './PlayerDeckContainer/PlayerDeckContainer';
import { Grid, GridInner, GridRowSlot, GridWrapper, LoadMoreButton, RowNavArrow, } from './GridComponent.styles';
import { ScrollDirectionEnum } from './GridConstants';
import { PlaylistThumbnail } from './Playlist';
import { Thumbnail } from './Thumbnail';
import { useGridScroll } from './useGridScroll';
/**
 * Fragment only expects a key prop and children.
 * We conditionally pick the container based on the showV2Card prop
 * so the component has to be able to take the V2 props while
 * conditionally returning the v2 component or a Fragment which throws
 * a type error if given the V2 props.
 */
function ThumbnailContainer(props) {
    if (props.showV2Card) {
        return (<ThumbnailContainerV2 {...{
            ...props,
            showV2Card: undefined,
        }}>
        {props.children}
      </ThumbnailContainerV2>);
    }
    if (isTruthy(props.params.player_deck_layout)) {
        return (<PlayerDeckContainer {...props}>{props.children}</PlayerDeckContainer>);
    }
    return <>{props.children}</>;
}
/**
 * Grid's inner component to display videos thumbnails.
 */
export const GridComponent = ({ appContext, columns, largeCarousel, params, videos, fetchMoreVideos, playlists, showV2Card, columnWidth, responsive, }) => {
    const { mode, max_videos, paginate_by, dir } = params;
    const { t } = useContextTranslation();
    const gridRef = useRef(null);
    const scrollRef = useRef(null);
    useGridScroll(scrollRef);
    const inViewportIds = useEmbedFeedStore((state) => state.inViewportIds);
    const [[itemsLimit, itemsToRemove], setItemsLimit] = useState([0, 0]);
    const [pageNumber, setPageNumber] = useState(1);
    const [{ showLeftRowNav, showRightRowNav }, setRowNav] = useState({
        showLeftRowNav: false,
        showRightRowNav: false,
    });
    const [scrolled, setScrolled] = useState(0);
    const [activePulseIndex, setActivePulseIndex] = useState(0);
    const playerState = useGlobalStore(playerStateOpenedSelector);
    const isPipActive = playerState?.layout === PlayerLayout.MINIMIZED;
    const trackEmbedImpression = useTrackEmbedImpression();
    const dispatchVideoEvent = useDispatchEmbedImpression();
    const trackThumbnailImpressionsEmbedFeed = useTrackThumbnailImpressionsEmbedFeed();
    const handleFeedImpression = useCallback(() => {
        // Send pixel tracking event
        trackEmbedImpression(appContext);
        // Send custom event
        dispatchVideoEvent({
            type: EventType.FW_EMBEDFEED_IMPRESSION,
        });
    }, [appContext, trackEmbedImpression, dispatchVideoEvent]);
    useLayoutEffect(() => {
        const grid = scrollRef.current;
        if (grid && (mode === 'row' || isStoriesPresentation)) {
            const scrollListener = (event) => {
                const { scrollLeft, offsetWidth, scrollWidth } = event.target;
                let newState = {
                    showLeftRowNav: false,
                    showRightRowNav: false,
                };
                if (isRTL(dir)) {
                    newState = {
                        showLeftRowNav: Math.abs(scrollLeft) > scrollWidth - (offsetWidth + 1) &&
                            videos.length > 0,
                        showRightRowNav: !(scrollLeft < 0),
                    };
                }
                else {
                    newState = {
                        showLeftRowNav: scrollLeft > 0,
                        showRightRowNav: scrollLeft < scrollWidth - offsetWidth && videos.length > 0,
                    };
                }
                if (showLeftRowNav !== newState.showLeftRowNav ||
                    (showRightRowNav !== newState.showRightRowNav && !isRTL(dir))) {
                    setRowNav(newState);
                }
                if ((isRTL(dir) && !(showLeftRowNav !== newState.showLeftRowNav)) ||
                    showRightRowNav !== newState.showRightRowNav) {
                    setRowNav(newState);
                }
            };
            scrollListener({ target: grid });
            grid.addEventListener('scroll', scrollListener, {
                passive: true,
            });
            const resizeListener = () => scrollListener({ target: grid });
            window.addEventListener('resize', resizeListener, { passive: true });
            return () => {
                grid.removeEventListener('scroll', scrollListener);
                window.removeEventListener('resize', resizeListener);
            };
        }
        // TODO(ZE-231): Fix the deps and re-enable the rule
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showLeftRowNav, showRightRowNav, videos.length, mode]);
    useLayoutEffect(() => {
        const grid = gridRef.current;
        if (grid) {
            if (mode === 'grid') {
                const limit = paginate_by && paginate_by > 0
                    ? pageNumber * (Math.floor(paginate_by / columns) * columns)
                    : max_videos;
                if (limit !== undefined && limit > 0) {
                    const maxVideos = Math.max(columns, limit);
                    const removeExtra = itemsToRemove !== columns ? itemsToRemove : 0;
                    const remove = maxVideos % columns;
                    const add = columns * Math.ceil(maxVideos / columns) - maxVideos;
                    if (remove <= Math.floor(columns / 2)) {
                        setItemsLimit([maxVideos - remove - removeExtra, itemsToRemove]);
                    }
                    else {
                        setItemsLimit([maxVideos + add - removeExtra, itemsToRemove]);
                    }
                }
            }
        }
    }, [
        videos.length,
        columns,
        max_videos,
        mode,
        itemsToRemove,
        pageNumber,
        paginate_by,
        gridRef,
    ]);
    const { isPlayingCurrentContent, currentPlayerVideoId } = usePlayerStateListener(appContext, params);
    useUpdateGridAutoplay({
        isPlayingCurrentContent,
        videos,
        autoplay: !!params.autoplay && !params.autoplay_keep_thumbnail,
    });
    const autoplayId = useGridAutoplayVideoId({ isPlayingCurrentContent });
    const autoplayIdRef = useRef(autoplayId);
    useHandleEmbedEngagedVideo({
        isPlayerDeckEmbed: params.player_deck_layout ?? false,
        appContext,
    });
    useTrackEmbedInteractableEngagedVideo({
        appContext,
    });
    const navigationForGrid = useMemo(() => (itemsLimit > 0 ? videos.slice(0, itemsLimit) : videos), [itemsLimit, videos]);
    const onScrollClick = (direction) => (event) => {
        event.preventDefault();
        const ids = sortByOtherArray(filterCurrentInViewport(inViewportIds), videos.map((video) => video.encoded_id));
        let scrollToVideo;
        const moveBackInItems = () => {
            setScrolled(scrolled - 1);
            const lastVisibleId = ids[0];
            const lastVisibleIndex = videos.findIndex((video) => video.encoded_id === lastVisibleId);
            scrollToVideo = videos[lastVisibleIndex - 1] || videos[lastVisibleIndex];
        };
        const moveAheadInItems = () => {
            setScrolled(scrolled + 1);
            const lastVisibleId = ids.slice(-1).pop();
            const lastVisibleIndex = videos.findIndex((video) => video.encoded_id === lastVisibleId);
            scrollToVideo = videos[lastVisibleIndex + 1] || videos[lastVisibleIndex];
        };
        if ((direction === ScrollDirectionEnum.LEFT && !isRTL(dir)) ||
            (direction === ScrollDirectionEnum.RIGHT && isRTL(dir))) {
            moveBackInItems();
        }
        else {
            moveAheadInItems();
        }
        const rootNode = scrollRef.current?.getRootNode();
        const target = rootNode?.querySelector(`.scrollTo_${scrollToVideo?.encoded_id}`);
        if (scrollRef.current && target) {
            if (direction === ScrollDirectionEnum.LEFT) {
                scrollRef.current.scrollLeft =
                    target.offsetLeft +
                        target.offsetWidth -
                        scrollRef.current.clientWidth;
            }
            else {
                scrollRef.current.scrollLeft = target.offsetLeft;
            }
        }
        if (!params.a11y_disable_focus) {
            // ADA: Focus first visible thumb after scrolling
            //  - find focusable thumbnail in viewport (tabindex != -1)
            //  - use setTimeout to overcome smooth scrolling
            setTimeout(() => {
                const firstTappable = scrollRef.current?.querySelector('button.focusable-play-button:not([tabindex="-1"])');
                firstTappable?.focus();
            }, 500);
        }
    };
    /**
     * @param {any} dir:string
     * @returns {boolean}
     */
    const isRTL = (dir = '') => {
        return dir === 'rtl';
    };
    const nextVideosLabel = t('Next Videos');
    const previousVideosLabel = t('Previous Videos');
    const navArrowLeftLabel = isRTL(dir) ? nextVideosLabel : previousVideosLabel;
    const navArrowRightLabel = isRTL(dir) ? previousVideosLabel : nextVideosLabel;
    const prefersReducedMotion = useReducedMotion();
    const onCartDisplayed = useGlobalStore(cartDisplayedCallbackSelector);
    const presentation = getPresentationFromParams(appContext.appContextType, params);
    const isStoriesPresentation = presentation === IPresentation.CIRCLE_STORIES ||
        presentation === IPresentation.VERTICAL_STORIES;
    useEffect(() => {
        if (isTruthy(params.player_deck_layout)) {
            onCartDisplayed?.();
        }
    }, [onCartDisplayed, params.player_deck_layout]);
    useEffect(() => {
        // only fw-stories have pulse animation
        if (!isStoriesPresentation) {
            return;
        }
        autoplayIdRef.current = autoplayId;
        if (autoplayId) {
            const newIndex = videos.findIndex((v) => v.encoded_id === autoplayId);
            if (newIndex !== -1 && newIndex !== activePulseIndex) {
                setActivePulseIndex(-1);
                // if autoplayId doesn'tchange for 10 seconds, update the activePulseIndex
                const timer = setTimeout(() => {
                    if (autoplayIdRef.current === autoplayId) {
                        setActivePulseIndex(newIndex);
                    }
                }, 10000);
                return () => clearTimeout(timer);
            }
        }
        else {
            if (activePulseIndex !== 0) {
                setActivePulseIndex(0);
            }
        }
    }, [autoplayId, videos, activePulseIndex, isStoriesPresentation]);
    const content = (<GridWrapper borderStyle={params.ui_border_style} ref={gridRef}>
      <GridInner>
        {(mode === 'row' || isStoriesPresentation) &&
            (isRTL(dir) ? !showLeftRowNav : showLeftRowNav) && (<RowNavArrow aria-label={navArrowLeftLabel} className="cy-scroll-left fw-feed-navigation" direction={ScrollDirectionEnum.LEFT} img={assetPinnedLeftArrow} onClick={onScrollClick(ScrollDirectionEnum.LEFT)} title={navArrowLeftLabel}/>)}
        <Grid columns={columns} gridMode={mode} ref={scrollRef} pulseAnimation={isStoriesPresentation && !!params.pulse_animation} widgetSize={params.size}>
          {playlists?.length > 0
            ? playlists.map((playlist, i) => {
                return (<Fragment key={playlist.id}>
                    <PlaylistThumbnail showV2Card={showV2Card} index={i} isLarge={largeCarousel} key={playlist.id} params={params} playlist={playlist} onFeedImpression={handleFeedImpression}/>
                  </Fragment>);
            })
            : navigationForGrid.map((video, i) => {
                return (<Fragment key={video.encoded_id}>
                    {mode === 'grid' && i % columns === 0 && (<GridRowSlot name={`grid-row-${i / columns}`}/>)}

                    <ThumbnailContainer appContext={appContext} params={params} video={video} showV2Card={showV2Card} key={video.encoded_id} responsive={responsive} columnWidth={columnWidth}>
                      <Thumbnail showV2Card={showV2Card} appContext={appContext} fetchMoreVideos={mode === 'grid' && paginate_by
                        ? undefined
                        : fetchMoreVideos} hasPlayingInPlayerOverlay={currentPlayerVideoId === video.encoded_id} index={i} isLarge={largeCarousel} isSmall={params.size === WidgetSizeEnum.SMALL &&
                        params.mode !== 'pinned'} key={video.encoded_id} params={params} total={navigationForGrid.length} onFeedImpression={handleFeedImpression} trackThumbnailImpressionsEmbedFeed={trackThumbnailImpressionsEmbedFeed} video={video} hasAutoplay={!prefersReducedMotion &&
                        video.encoded_id === autoplayId} responsive={responsive} shouldPulse={isStoriesPresentation &&
                        i === activePulseIndex &&
                        !isPipActive}/>
                    </ThumbnailContainer>
                  </Fragment>);
            })}

          {mode === 'grid' && paginate_by && (<LoadMoreButton className="cy-load-more-button" onClick={() => {
                setPageNumber(pageNumber + 1);
                fetchMoreVideos();
            }}>
              {t('Load More')}
            </LoadMoreButton>)}
        </Grid>
        {(mode === 'row' || isStoriesPresentation) &&
            (isRTL(dir) ? !showRightRowNav : showRightRowNav) && (<RowNavArrow aria-label={navArrowRightLabel} className="cy-scroll-right fw-feed-navigation" direction={ScrollDirectionEnum.RIGHT} img={assetPinnedRightArrow} onClick={onScrollClick(ScrollDirectionEnum.RIGHT)} title={navArrowRightLabel}/>)}
      </GridInner>
    </GridWrapper>);
    return (<Fragment>
      {mode === 'pinned' ? (<PinnedWrapper onScrollLeft={onScrollClick(ScrollDirectionEnum.LEFT)} onScrollRight={onScrollClick(ScrollDirectionEnum.RIGHT)} showLeftNav={!!scrolled} title={params.title}>
          {content}
        </PinnedWrapper>) : (content)}
    </Fragment>);
};
export default GridComponent;
